import {
  useV2CreateQuestionMutation,
  useV2UpdateQuestionMutation,
  useV2DeleteQuestionMutation,
} from "@generated";
import type { V2QuestionResource } from "@generated";
import {
  hasMinMax,
  hasStep,
  hasSelectOptions,
  type QuestionFormValues,
} from "../types";

// Keep only the settings keys relevant to the value-type. min/max are strings,
// step is the integer 0|1|2 (number type only).
const buildSettings = (values: QuestionFormValues) => {
  const s: { min?: string; max?: string; step?: number } = {};
  if (hasMinMax(values.valueType)) {
    s.min = values.settings.min;
    s.max = values.settings.max;
  }
  if (hasStep(values.valueType) && values.settings.step !== "") {
    s.step = Number(values.settings.step);
  }
  return s;
};

// Drop empty-key rows; keep at most one default (the first).
const buildSelectOptions = (values: QuestionFormValues) => {
  if (!hasSelectOptions(values.valueType)) return [];
  let seenDefault = false;
  return values.selectOptions
    .filter((o) => o.key.trim() !== "")
    .map((o) => {
      const isDefault = o.default && !seenDefault;
      if (isDefault) seenDefault = true;
      return { key: o.key, value: o.value, default: isDefault };
    });
};

export const buildQuestionData = (
  values: QuestionFormValues,
  questionnaireId: string,
  sectionId: string,
  id?: string
): V2QuestionResource => {
  const attributes: any = {
    question: values.question,
    name: values.name,
    required: values.required,
    settings: buildSettings(values),
    "select-options": buildSelectOptions(values),
  };
  // value-type is locked after create — only send it when creating.
  if (!id) attributes["value-type"] = values.valueType;

  return {
    type: "questions",
    ...(id ? { id } : {}),
    attributes,
    relationships: {
      questionnaire: { data: { id: questionnaireId, type: "questionnaires" } },
      "questionnaire-section": {
        data: { id: sectionId, type: "questionnaire-sections" },
      },
    },
  } as unknown as V2QuestionResource;
};

// Position-only PATCH used by drag-reorder: never touches value-type/settings/
// options. Always sends the section relationship so a cross-section move
// reassigns the parent in the same request.
export const buildQuestionPositionData = (
  id: string,
  position: number,
  questionnaireId: string,
  sectionId: string
): V2QuestionResource =>
  ({
    type: "questions",
    id: id,
    attributes: { position },
    relationships: {
      questionnaire: { data: { id: questionnaireId, type: "questionnaires" } },
      "questionnaire-section": {
        data: { id: sectionId, type: "questionnaire-sections" },
      },
    },
  } as unknown as V2QuestionResource);

export const useQuestionMutations = (questionnaireId: string) => {
  const [create] = useV2CreateQuestionMutation();
  const [update] = useV2UpdateQuestionMutation();
  const [destroy] = useV2DeleteQuestionMutation();
  const qid = Number(questionnaireId);

  return {
    create: (values: QuestionFormValues, sectionId: string) =>
      create({
        questionnaireId: qid,
        body: { data: buildQuestionData(values, questionnaireId, sectionId) },
      }).unwrap(),
    update: (id: string, values: QuestionFormValues, sectionId: string) =>
      update({
        questionnaireId: qid,
        id: id,
        body: {
          data: buildQuestionData(values, questionnaireId, sectionId, id),
        },
      }).unwrap(),
    remove: (id: string) => destroy({ questionnaireId: qid, id: id }).unwrap(),
    reorder: (id: string, position: number, sectionId: string) =>
      update({
        questionnaireId: qid,
        id: id,
        body: {
          data: buildQuestionPositionData(
            id,
            position,
            questionnaireId,
            sectionId
          ),
        },
      }).unwrap(),
  };
};

export default useQuestionMutations;
